import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import api from "../lib/api";
import { 
  TrendingUp, 
  Users, 
  Eye, 
  EyeOff, 
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Home,
  UserPlus,
  Ticket,
  CheckCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function OnboardingPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Beta gate
  const [betaValidated, setBetaValidated] = useState(user?.beta_access === true);
  const [betaCode, setBetaCode] = useState("");
  const [betaError, setBetaError] = useState("");
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaSuccess, setBetaSuccess] = useState("");
  
  // Household data
  const [householdName, setHouseholdName] = useState("");
  const [currency, setCurrency] = useState("NZD");
  const [householdStyle, setHouseholdStyle] = useState("shared");
  const [inviteEmail, setInviteEmail] = useState("");

  const householdStyles = [
    {
      value: "shared",
      title: "Fully Shared",
      description: "Complete transparency. All accounts and spending visible to everyone.",
      icon: Eye
    },
    {
      value: "shared_with_private",
      title: "Shared + Private Discretionary",
      description: "Shared bills and savings, but personal spending totals stay private.",
      icon: EyeOff
    },
    {
      value: "separate",
      title: "Separate with Shared Goals",
      description: "Separate finances but collaborate on household goals.",
      icon: Users
    }
  ];

  // Redeem beta code
  const handleRedeemCode = async () => {
    if (!betaCode.trim()) {
      setBetaError("That code doesn't look right. Check it and try again.");
      return;
    }
    setBetaLoading(true);
    setBetaError("");
    try {
      const res = await api.beta.redeemCode(betaCode.trim());
      if (res.ok) {
        const data = await res.json();
        if (data.valid) {
          setBetaSuccess(data.message || "You're in — let's get started.");
          // Brief pause to show success
          setTimeout(async () => {
            await checkAuth(); // Refresh user with beta_access: true
            setBetaValidated(true);
          }, 1200);
        } else {
          setBetaError(data.error || "That code doesn't look right. Check it and try again.");
        }
      } else {
        setBetaError("That code doesn't look right. Check it and try again.");
      }
    } catch {
      setBetaError("That code doesn't look right. Check it and try again.");
    } finally {
      setBetaLoading(false);
    }
  };

  const handleCreateHousehold = async () => {
    if (!householdName.trim()) {
      toast.error("Please enter a household name");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/households`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: householdName,
          currency,
          timezone: "Pacific/Auckland",
          style: householdStyle,
        })
      });

      // Parse response once
      const contentType = response.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");
      const result = isJson ? await response.json() : await response.text();

      if (!response.ok) {
        const errorMessage = result?.detail || result?.message || 'Failed to create household';
        throw new Error(errorMessage);
      }

      toast.success("Household created");
      setStep(3);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInvitePartner = async () => {
    if (!inviteEmail.trim()) {
      await checkAuth();
      navigate("/onboarding-flow");
      return;
    }

    setLoading(true);
    try {
      await checkAuth();
      toast.success("Invitation sent (Demo mode - invite shown)");
      navigate("/onboarding-flow");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipInvite = async () => {
    await checkAuth();
    navigate("/onboarding-flow");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Progress */}
      <div className="hidden lg:flex lg:w-1/3 bg-[#1a4d3e] dark:bg-[#0f3d2e] flex-col justify-between p-12">
        <div>
          <div className="flex items-center gap-3 text-white mb-16">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight">ARA Wealth</span>
          </div>

          <div className="space-y-8">
            {betaValidated ? (
              <>
                {[
                  { num: 1, title: "Choose Style", desc: "How your household manages finances" },
                  { num: 2, title: "Name Your Household", desc: "Give it a name and select currency" },
                  { num: 3, title: "Invite Partner", desc: "Optionally invite your partner" }
                ].map((s) => (
                  <div key={s.num} className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step >= s.num ? 'bg-white text-emerald-700' : 'bg-emerald-600 text-white'
                    }`}>
                      {step > s.num ? '✓' : s.num}
                    </div>
                    <div>
                      <h4 className={`font-medium ${step >= s.num ? 'text-white' : 'text-emerald-200'}`}>
                        {s.title}
                      </h4>
                      <p className="text-sm text-emerald-200/70">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <p className="text-emerald-200/80 text-sm leading-relaxed">
                Enter your invite code to continue to onboarding.
              </p>
            )}
          </div>
        </div>

        <div className="text-emerald-200 text-sm">
          Welcome, {user?.name || 'there'}! Let's set up your household.
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-xl">

          {/* Beta Gate */}
          {!betaValidated && (
            <div className="animate-fade-in" data-testid="beta-gate">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
                  <Ticket className="w-8 h-8 text-[#1a4d3e]" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">Enter your beta invite code</h1>
                <p className="text-muted-foreground">
                  This code gives you access to the beta while we test and refine the experience.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="betaCode">Invite code</Label>
                  <Input
                    id="betaCode"
                    type="text"
                    placeholder="ARA-XXXXX"
                    value={betaCode}
                    onChange={(e) => { setBetaCode(e.target.value.toUpperCase()); setBetaError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleRedeemCode(); }}
                    className={`h-12 text-lg tracking-widest font-mono ${
                      betaError ? "border-red-400" : betaSuccess ? "border-green-500 bg-green-50 dark:bg-green-950/30" : ""
                    }`}
                    disabled={!!betaSuccess}
                    data-testid="beta-code-input"
                  />
                </div>

                {betaError && (
                  <p className="text-sm text-red-500" data-testid="beta-code-error">{betaError}</p>
                )}

                {betaSuccess && (
                  <div className="flex items-center gap-2 text-sm text-green-600" data-testid="beta-code-success">
                    <CheckCircle className="w-4 h-4" />
                    <span>{betaSuccess}</span>
                  </div>
                )}

                <Button
                  className="btn-primary w-full h-12"
                  onClick={handleRedeemCode}
                  disabled={betaLoading || !!betaSuccess || !betaCode.trim()}
                  data-testid="beta-code-submit"
                >
                  {betaLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : betaSuccess ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 1: Choose Style */}
          {betaValidated && step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">Choose Your Style</h1>
                <p className="text-muted-foreground">
                  How would you like to manage finances as a household?
                </p>
              </div>

              <RadioGroup value={householdStyle} onValueChange={setHouseholdStyle} className="space-y-4">
                {householdStyles.map((style) => (
                  <Label
                    key={style.value}
                    htmlFor={style.value}
                    className={`flex items-start gap-4 p-6 rounded-xl border-2 cursor-pointer transition-all ${
                      householdStyle === style.value
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30'
                        : 'border-border hover:border-emerald-300'
                    }`}
                    data-testid={`style-${style.value}`}
                  >
                    <RadioGroupItem value={style.value} id={style.value} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <style.icon className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium">{style.title}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{style.description}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>

              <Button 
                className="btn-primary w-full h-12 mt-8"
                onClick={() => setStep(2)}
                data-testid="continue-to-step-2"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Name Household */}
          {betaValidated && step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mx-auto mb-4">
                  <Home className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">Name Your Household</h1>
                <p className="text-muted-foreground">
                  Give your household a name and select your currency.
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="householdName">Household Name</Label>
                  <Input
                    id="householdName"
                    type="text"
                    placeholder="e.g., The Smith Family"
                    value={householdName}
                    onChange={(e) => setHouseholdName(e.target.value)}
                    className="h-12"
                    data-testid="household-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <select
                    id="currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full h-12 rounded-lg border border-input bg-background px-4"
                    data-testid="currency-select"
                  >
                    <option value="NZD">NZD - New Zealand Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="EUR">EUR - Euro</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <Button 
                  variant="outline" 
                  className="flex-1 h-12"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Back
                </Button>
                <Button 
                  className="btn-primary flex-1 h-12"
                  onClick={handleCreateHousehold}
                  disabled={loading}
                  data-testid="create-household-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Create Household
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Invite Partner */}
          {betaValidated && step === 3 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">Invite Your Partner</h1>
                <p className="text-muted-foreground">
                  ARA Wealth is better together. Invite your partner to collaborate.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="inviteEmail">Partner's Email (Optional)</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  placeholder="partner@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="h-12"
                  data-testid="invite-email-input"
                />
              </div>

              <div className="flex flex-col gap-4 mt-8">
                <Button 
                  className="btn-primary h-12"
                  onClick={handleInvitePartner}
                  disabled={loading}
                  data-testid="send-invite-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : inviteEmail ? (
                    <>
                      Send Invite & Continue
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  ) : (
                    <>
                      Continue to Dashboard
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                
                {inviteEmail && (
                  <Button 
                    variant="ghost" 
                    className="h-12"
                    onClick={handleSkipInvite}
                    data-testid="skip-invite-btn"
                  >
                    Skip for now
                  </Button>
                )}
              </div>

              <div className="mt-8 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Bonus XP Awaits
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      Complete your first reconciliation together to earn 100 bonus XP.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
